/* eslint-disable camelcase */
import app from 'firebase/app';
import 'firebase/database';
import 'firebase/auth';
import 'firebase/storage';
import * as Sentry from '@sentry/browser';
import { Cookies } from 'react-cookie';
import _ from 'lodash';
import { SENTRY } from '../constants/shopId';
import store from '../utils/store';
import { isStringNotNull, isObjectNotEmpty } from '../utils/tools';
import { applicationError } from '../utils/applicationError';

import { round2, failSafe } from 'topkat-utils';

import {
  SET_SUPPORT,
  SET_FIREBASE,
  SET_ORDERS,
  RESET_REALTIME_DB,
  RESET_ORDERS,
  RESET_FINANCE,
  SIGN_OUT_SUCCESS,
  SET_MENU_REALTIME,
} from '../actions/types/index';
import {
  setLoggedInUser,
  setLogOut,
  intializingData,
  openErrorModal,
} from '../actions';
import {
  ERROR_NO_ACCESS,
  ERROR_SHOP_NOT_FOUND,
  INDEPENDANT,
  CHAIN,
  FRANCHISE,
  ERROR_GET_FINANCES,
  ONETOUCH,
  LEVELS,
} from '../constants';
import isProd from '../utils/getEnv';
import {
  initializeBackend,
  uploadImage,
  getBilling,
  getAllDrivers,
  updateShopPrinter,
  getCompanyConfiguration,
  getOneShop,
  updateWebappCartPhrase,
  updateDesignWeb,
  updateNewProducts,
  updateDesignMobile,
  updateConfigurationOrderSettings,
  getCompanyFirebaseCredentials,
  getConfigurationServicePrice,
  getCompanyIds,
  getRolesAndPermissions,
  getHotlineAvailability,
} from './backend';
import { getUserPermissions } from '../utils/admins';
import { DISHOP_INFO } from '../constants/dishopInfo';
import {
  calculatePreviousPeriod,
  calculateCost,
  calculateTotalTabForShop,
} from '../pages/Finances/Finances.services';
import { isFreelanceEnabled } from '../utils/delivery';
import {
  checkIntegrationAccess,
  hasExternalIntegration,
} from 'src/utils/externalIntegration';
import { logEvent } from '../utils/logger';
import moment from 'moment';
let independantDriverInterval = null;
let driverInterval = null;
let initializedApp;
let initializedDeliveryBDD;
let shopConfig = null;
let shopIdCache = '';
const firebaseConfigsForShops = {};
let listenAuthStateChange = false;
let unlistenAuthStateChange = false;
let isAllShopsGlobal = false;
let cachedToken = null;
let expiresAt = 0;
const SKEW_MS = 60 * 1000;

// TODO this file should be separated in modules. Eg: config.service.js firebaseOn.js...etc)

export const listenAuth = async (shopId) => {
  if (listenAuthStateChange !== shopId) {
    listenAuthStateChange = shopId;
    if (unlistenAuthStateChange) unlistenAuthStateChange();

    const auth = initializedApp.auth();
    unlistenAuthStateChange = auth.onAuthStateChanged(async (firebaseUser) => {
      try {
        await getIdToken(initializedApp);
        const allowConnexion = new Cookies().get('allowConnexion');
        if (firebaseUser && firebaseUser.uid && allowConnexion) {
          const shopIdSelected = new Cookies().get('shopIdSelected');
          const isAllShops = JSON.parse(
            new Cookies().get('isAllShops') || false,
          );
          store.dispatch(setLoggedInUser(shopIdSelected || shopId, isAllShops));
        } else {
          unlistenFirebase();
          new Cookies().remove('shopId', { path: '/' });
          new Cookies().remove('shopIdSelected', { path: '/' });
          new Cookies().remove('isAllShops', { path: '/' });
          new Cookies().remove('allowConnexion', { path: '/' });
          store.dispatch(setLogOut());
          store.dispatch(intializingData(false));
          resetReduxData();
          listenAuthStateChange = false;
          unlistenAuthStateChange = false;
        }
      } catch (err) {
        console.error(err);
      }
    });
  }
};

async function setShopId(shopId) {
  if (shopIdCache !== shopId) {
    shopIdCache = shopId;
    await getFirebaseCredentials(shopId);
  }
  return shopIdCache;
}

export function getShopId() {
  return shopIdCache;
}

export const getFirebaseCustomerId = () => {
  const { currentUser = {} } = initializedApp?.auth() || {};
  return currentUser?.uid || null;
};

export const signInUser = async (email, password, shopId) => {
  try {
    logEvent({
      level: LEVELS.INFO,
      message: `Logging in user ${email}`,
    });
    await setShopId(shopId);
    if (shopId in firebaseConfigsForShops) {
      shopConfig = firebaseConfigsForShops[shopId];
      await Promise.all([
        // perf improvement
        initializeApp(),
        initializeSentry(shopId),
        initializedApp.auth().signInWithEmailAndPassword(email, password),
      ]);
      logEvent({
        level: LEVELS.INFO,
        message: 'Success login user',
      });
      const idToken = await getIdToken(initializedApp, true);

      const { allowConnexion, isSuperAdmin } = await getUserPermissions(shopId);

      if (!allowConnexion) throw ERROR_NO_ACCESS; // TODO refactor with DescriptiveError
      const {
        id: companyId = '',
        dashboardUrl,
      } = await getCompanyConfiguration(
        firebaseConfigsForShops[shopId]?.companyId,
        idToken,
      );
      if (
        isProd() &&
        dashboardUrl &&
        !window.location.href.includes(dashboardUrl)
      ) {
        window.location.href = dashboardUrl;
      }
      // 1st try local =>      2534.85498046875 ms
      // 2nd try local =>      2341.534912109375 ms
      // 1st try newVersion => 1290.8720703125 ms
      // 2nd try newVersion => 1077.10205078125 ms
      const shopIds = await getCompanyIds(companyId, idToken);
      const isAllShops =
        shopId === companyId && isSuperAdmin && shopIds.length > 1;
      if (isAllShops || shopIds?.includes(shopId)) {
        const cookies = new Cookies();
        const expireDate = new Date();
        expireDate.setYear(expireDate.getFullYear() + 1);
        const cookieOptions = { expires: expireDate, path: '/' };
        await cookies.set('shopId', shopId, cookieOptions);
        await cookies.set('shopIdSelected', shopId, cookieOptions);
        await cookies.set('isAllShops', isAllShops, cookieOptions);
        listenAuth(shopId);
        await setShopId(shopId);
      } else {
        throw ERROR_SHOP_NOT_FOUND;
      }
    } else {
      throw ERROR_SHOP_NOT_FOUND;
    }
  } catch (error) {
    throw error.code || error;
  }
};

export const resetPasswordUser = async (email, shopId) => {
  try {
    if (shopId in firebaseConfigsForShops) {
      logEvent({
        level: LEVELS.INFO,
        message: 'resetting password',
      });
      shopConfig = firebaseConfigsForShops[shopId];
      await setShopId(shopId);
      await initializeApp();
      await initializedApp.auth().sendPasswordResetEmail(email);
    } else {
      throw ERROR_SHOP_NOT_FOUND;
    }
  } catch (error) {
    throw error.code || error; // TODO DescriptiveError 2
  }
};

export const getInits = async (shopId, userPermissions, isAllShops) => {
  const idToken = await getIdToken(initializedApp, true);
  const {
    domain = '',
    businessType = '',
    id = '',
    paymentTypes = [],
  } = await getCompanyConfiguration(
    firebaseConfigsForShops[shopId]?.companyId,
    idToken,
  );
  initializeBackend(domain);
  await setShopId(shopId);
  const { generalConfig } = store.getState().firebaseRealTime;
  const shopIds = await getCompanyIds(
    firebaseConfigsForShops[shopId]?.companyId,
    idToken,
  );
  const nbShops = isObjectNotEmpty(shopIds) ? _.size(shopIds) : 1;
  const {
    printers = {},
    servicePrice = {},
    address = {},
    legal = {},
    configuration = {},
    name,
    type,
  } = !isAllShops ? await getOneShop(id, shopIdCache, idToken) : {};

  const { roleForCompany, isAdminOfThisShop } = userPermissions;

  let { servicePrice: configServicePrice = {} } = generalConfig;
  const {
    networkId = false,
    legal: networkLegal = DISHOP_INFO,
  } = configServicePrice;
  if (networkId) {
    // TODO obsolete code ?? also comapre with provided shopId, if they are the same
    const {
      servicePrice: networkConfigServicePrice = {},
    } = await getConfigurationServicePrice(networkId, idToken);
    configServicePrice = networkConfigServicePrice;
  }
  const { legal: configLegal } = configServicePrice;

  const creditorNetwork = networkId ? configLegal : DISHOP_INFO;
  const {
    orderTypes = ['delivery', 'click_and_collect'],
    dashboard = {},
  } = configuration;
  const {
    scheduleTimeWindow = 10000,
    contactingButtons = ['15 mins', '30 mins', '45 mins'],
  } = dashboard;

  const { storageBucket } = firebaseConfigsForShops[shopId] || {};
  const initDatas = {
    domainApi: domain,
    businessType,
    printers,
    servicePrice,
    legal,
    networkLegal,
    address,
    shopId,
    storageBucket,
    companyId: id,
    orderTypes,
    creditorNetwork,
    scheduleTimeWindow,
    dashboardConfig: dashboard,
    paymentTypes,
    contactingButtons,
    oneTouchEnabled: hasExternalIntegration(shopId) === ONETOUCH,
    generalAdmin:
      nbShops === 1 && roleForCompany === 'companyAdmin'
        ? true
        : roleForCompany === 'companyAdmin',
    independantShopAdmin: isAdminOfThisShop && type === INDEPENDANT,
    franchiseShopAdmin: isAdminOfThisShop && (type === FRANCHISE || !type),
    chainShopAdmin: isAdminOfThisShop && type === CHAIN,
    nbShops,
    name,
    configLegal,
  };

  return { initDatas, initializedApp };
};

export const setFirebaseRealTime = async (
  dispatch,
  shopId,
  userPermissions,
  isAllShops,
  isShopChanged,
) => {
  const oldShopId = shopIdCache;
  await setShopId(shopId);

  isAllShopsGlobal = isAllShops;
  if (isShopChanged) {
    const { shops = {} } = store.getState().firebaseRealTime;
    const shopsLoaded = Object.keys(shops).length > 0;

    if (!shopsLoaded || isAllShops) {
      await setShopsRealTime(dispatch);
    }

    const { shops: updatedShops = {} } = store.getState().firebaseRealTime;
    const shop = updatedShops?.[shopId] ?? null;
    if (shop && !isAllShops) {
      dispatch({ type: SET_FIREBASE, payload: shop, id: 'shop' });
      await listenMenuRealTime(dispatch, shop, oldShopId);
    } else {
      dispatch({ type: SET_FIREBASE, payload: {}, id: 'shop' });
      dispatch({
        type: SET_MENU_REALTIME,
        payload: { menu: {}, menuShopId: null },
      });
    }

    await setGeneralConfigRealTime(dispatch);
    await followOrders(dispatch);
    await followSupport(dispatch);
    await setPushNotificationsRealTime(dispatch);
  } else {
    userPermissions?.isSuperAdmin
      ? await setShopsRealTime(dispatch)
      : userPermissions?.isAdminOfThisShop &&
      (await listenShopRealTime(dispatch, oldShopId));
    await setGeneralConfigRealTime(dispatch);
    await followOrders(dispatch);
    await followSupport(dispatch);
    await setPushNotificationsRealTime(dispatch);
  }
  await setExternalDriversRealTime(dispatch);
};

const getDriversByType = async (isIndependent, withCompletedDelivery) => {
  try {
    const snapshotDrivers = await getAllDrivers(
      isIndependent,
      withCompletedDelivery,
    );
    const id = isIndependent ? 'independentDrivers' : 'drivers';
    if (!_.isEqual(snapshotDrivers, store.getState().firebaseRealTime[id])) {
      store.dispatch({ type: SET_FIREBASE, payload: snapshotDrivers, id });
    }
  } catch (err) {
    console.error(err);
  }
};
const setExternalDriversRealTime = async (dispatch) => {
  try {
    const onValueChange = async (snapshot) => {
      const driversData = snapshot.val() ? snapshot.val() : null;
      dispatch({
        type: SET_FIREBASE,
        payload: driversData,
        id: 'externalDrivers',
      });
    };
    await initializedDeliveryBDD
      .database()
      .ref(`externalDrivers`)
      .off('value');
    await initializedDeliveryBDD
      .database()
      .ref('externalDrivers')
      .on('value', onValueChange);
    return true;
  } catch (err) {
    console.error(err);
    return err;
  }
};
export const fetchAllDrivers = async (
  interval,
  shopId,
  withCompletedDelivery,
) => {
  try {
    const { isAdminOfThisShop } = await getUserPermissions(shopId);
    const isFreelanceEnabld = isAdminOfThisShop
      ? await isFreelanceEnabled(shopId)
      : await isFreelanceEnabled();
    clearDriversInterval();
    await getDriversByType(false, withCompletedDelivery);
    driverInterval = setInterval(
      () => getDriversByType(false, withCompletedDelivery),
      interval,
    );
    if (isFreelanceEnabld) {
      await getDriversByType(true, withCompletedDelivery);
      independantDriverInterval = setInterval(
        () => getDriversByType(true, withCompletedDelivery),
        interval,
      );
    }
  } catch (err) {
    console.error(err);
    return err;
  }
};

export const clearDriversInterval = () => {
  clearInterval(independantDriverInterval);
  clearInterval(driverInterval);
};

const setGeneralConfigRealTime = (dispatch) => {
  const onValueChange = (snapshot) => {
    const generalConfig = snapshot.val() || null;
    dispatch({
      type: SET_FIREBASE,
      payload: generalConfig,
      id: 'generalConfig',
    });
  };
  initializedApp
    .database()
    .ref('configuration')
    .on('value', onValueChange);
  return true;
};

const resetReduxData = () => {
  store.dispatch({ type: RESET_REALTIME_DB });
  store.dispatch({ type: RESET_ORDERS });
  store.dispatch({ type: RESET_FINANCE });
  store.dispatch({ type: SIGN_OUT_SUCCESS });
};

export const signOut = async () => {
  cachedToken = null;
  expiresAt = 0;
  logEvent({
    level: LEVELS.INFO,
    message: `'Signing out customer'`,
  });
  await initializedApp.auth().signOut();
  await getIdToken(initializedApp, true);
};

export const updateOrderSettings = async (configurations) => {
  const { slotInterval, showOrderInPreparation } = configurations;
  await updateConfigurationOrderSettings({
    slotInterval: parseInt(slotInterval),
    showOrderInPreparation,
  });
};
const setShopsRealTime = async (dispatch) => {
  return await new Promise(async (resolve, reject) => {
    try {
      const onValueChange = async (snapshot) => {
        // TODO perf cost +++, why not using redux instead?
        const shops = snapshot.val() || {};
        const shop = shops[shopIdCache] ? shops[shopIdCache] : null;
        if (shop || isAllShopsGlobal) {
          if (!isAllShopsGlobal) {
            dispatch({ type: SET_FIREBASE, payload: shop, id: 'shop' });
            await listenMenuRealTime(dispatch, shop, shopIdCache);
          }
          dispatch({ type: SET_FIREBASE, payload: shops, id: 'shops' });
        } else {
          signOut();
        }
        resolve();
      };

      initializedApp
        .database()
        .ref('shops')
        .on('value', onValueChange);
    } catch (err) {
      console.error(err);
      reject(err);
    }
  });
};

const listenMenuRealTime = async (dispatch, shop, oldShopId) => {
  try {
    const onValueChange = async (snapshot) => {
      const menu = snapshot.val() || {};
      dispatch({
        type: SET_MENU_REALTIME,
        payload: { menu, menuShopId: shopIdCache },
      });
    };
    await initializedApp
      .database()
      .ref(`menus/${oldShopId}`)
      .off('value');
    await initializedApp
      .database()
      .ref(`menus/${shopIdCache}`)
      .on('value', onValueChange);
  } catch (err) {
    console.error(err);
  }
};

export const listenMenusPOSRealTime = async (dispatch, oldShopId) => {
  if (checkIntegrationAccess('products', 'hasMenuPOS')) {
    try {
      const onValueChange = async (snapshot) => {
        const menuPOS = snapshot.val() || {};
        dispatch({ type: SET_FIREBASE, payload: menuPOS, id: 'menuPOS' });
      };
      await initializedApp
        .database()
        .ref(`menusPOS/${oldShopId}`)
        .off('value');
      await initializedApp
        .database()
        .ref(`menusPOS/${shopIdCache}`)
        .on('value', onValueChange);
    } catch (err) {
      console.error(err);
    }
  }
};
const listenShopRealTime = async (dispatch, oldShopId) => {
  try {
    const onValueChange = async (snapshot) => {
      const shop = snapshot.val() || {};
      dispatch({ type: SET_FIREBASE, payload: shop, id: 'shop' });
      await listenMenuRealTime(dispatch, shop, shopIdCache);
    };
    await initializedApp
      .database()
      .ref(`shops/${oldShopId}`)
      .off('value');
    await initializedApp
      .database()
      .ref(`shops/${shopIdCache}`)
      .on('value', onValueChange);
  } catch (err) {
    console.error(err);
  }
};

const followOrders = async (dispatch) => {
  return await new Promise((resolve, reject) => {
    try {
      const orderValueChange = (snapshot) => {
        getAndFilterOrders(snapshot.val(), dispatch);
        resolve();
      };
      initializedApp
        .database()
        .ref('/orders')
        .on('value', orderValueChange);
    } catch (err) {
      console.error(err);
      reject(err);
    }
  });
};

const getAndFilterOrders = (data, dispatch) => {
  const newOrders = {};
  _.map(data, (customerOrders, customerId) => {
    _.map(customerOrders, (order, chargeId) => {
      if (order.id) {
        if (!newOrders[customerId]) {
          newOrders[customerId] = {};
        }
        newOrders[customerId][chargeId] = order;
      }
    });
  });
  dispatch({ type: SET_ORDERS, payload: newOrders });
};

export const getFinances = async (dateRange, type, shops, isAllShops) => {
  const stripeOrganize = {};
  const tpeOrganize = {};
  const cashOrganize = {};
  const totalOrganize = {};
  const ticket_restoOrganise = {};
  const counterOrganise = {};
  const stripe = [],
    tpe = [],
    cash = [],
    ticket_resto = [],
    total = [],
    counter = [],
    details = [];
  const newTab = () => [
    { name: "Chiffre d'affaires", cost: 0 },
    { name: "Couts d'achat produit", cost: 0 },
    { name: 'Marges brut', cost: 0 },
    { name: 'Nombre de commandes', cost: 0 },
    { name: 'Panier moyen', cost: 0 },
  ];
  // TODO this whole code should be in a for loop to avoid repeating so much code
  const [
    totalTab,
    stripeTab,
    tpeTab,
    cashTab,
    ticket_restoTab,
    counterTab,
  ] = Array.from({ length: 6 }, newTab);
  const shopResults = [];
  try {
    const previousPeriod = calculatePreviousPeriod(dateRange);
    const firstDate = moment(previousPeriod.startDate)
      .startOf('day')
      .utcOffset(0, true)
      .toISOString();

    const lastDate = moment(dateRange.endDate)
      .endOf('day')
      .utcOffset(0, true)
      .toISOString();
    let billing = await getBilling(
      isAllShops ? null : shopIdCache,
      firstDate,
      lastDate,
    );
    const date1Bis = moment(dateRange.startDate).startOf('day');
    const date2Bis = moment(lastDate).endOf('day');
    let increaseDate = `${date1Bis.date()}/${date1Bis.month()}`;
    const initializedObject = {
      date: moment(date1Bis),
      cumulDaily: 0,
    };

    while (date1Bis <= date2Bis) {
      stripeOrganize[increaseDate] = { ...initializedObject };
      ticket_restoOrganise[increaseDate] = { ...initializedObject };
      tpeOrganize[increaseDate] = { ...initializedObject };
      cashOrganize[increaseDate] = { ...initializedObject };
      totalOrganize[increaseDate] = { ...initializedObject };
      counterOrganise[increaseDate] = { ...initializedObject };
      date1Bis.add(1, 'day');
      initializedObject.date = moment(date1Bis);
      increaseDate = `${date1Bis.date()}/${date1Bis.month()}`;
    }

    if (billing) {
      Object.keys(billing).forEach((commande) => {
        const {
          priceOrder: price = 0,
          priceProduct: priceCost = 0,
          paymentType: type = '',
          dateOrder: orderDate = '',
        } = billing[commande];

        const orderDateObj = moment(orderDate);
        if (
          orderDateObj.isBetween(
            moment(dateRange.startDate).startOf('day'),
            moment(lastDate).endOf('day')
          )
        ) {
          const dateId = `${orderDateObj.date()}/${orderDateObj.month()}`;
          if (type === 'cash') {
            calculateCost(cashOrganize[dateId], cashTab, price, priceCost);
          } else if (type === 'stripe') {
            calculateCost(stripeOrganize[dateId], stripeTab, price, priceCost);
          } else if (type === 'ticket_resto') {
            calculateCost(
              ticket_restoOrganise[dateId],
              ticket_restoTab,
              price,
              priceCost,
            );
          } else if (type === 'counter') {
            calculateCost(
              counterOrganise[dateId],
              counterTab,
              price,
              priceCost,
            );
          } else {
            calculateCost(tpeOrganize[dateId], tpeTab, price, priceCost);
          }
          calculateCost(totalOrganize[dateId], totalTab, price, priceCost);
          details.push({
            price,
            orderDate,
            type,
          });
        }
      });

      const indices = [2, 4];
      if (totalTab[0].cost > 0) {
        for (const index of indices) {
          totalTab[index].cost =
            index === 2
              ? totalTab[0].cost - totalTab[1].cost
              : totalTab[0].cost / totalTab[3].cost || 0;
          stripeTab[index].cost =
            index === 2
              ? stripeTab[0].cost - stripeTab[1].cost
              : stripeTab[0].cost / stripeTab[3].cost || 0;
          counterTab[index].cost =
            index === 2
              ? counterTab[0].cost - counterTab[1].cost
              : counterTab[0].cost / counterTab[3].cost || 0;
          tpeTab[index].cost =
            index === 2
              ? tpeTab[0].cost - tpeTab[1].cost
              : tpeTab[0].cost / tpeTab[3].cost || 0;
          cashTab[index].cost =
            index === 2
              ? cashTab[0].cost - cashTab[1].cost
              : cashTab[0].cost / cashTab[3].cost || 0;
          ticket_restoTab[index].cost =
            index === 2
              ? ticket_restoTab[0].cost - ticket_restoTab[1].cost
              : ticket_restoTab[0].cost / ticket_restoTab[3].cost || 0;
        }
      }

      for (let i = 0; i < 5; i++) {
        stripeTab[i].cost = round2(stripeTab[i].cost);
        cashTab[i].cost = round2(cashTab[i].cost);
        totalTab[i].cost = round2(totalTab[i].cost);
        tpeTab[i].cost = round2(tpeTab[i].cost);
        counterTab[i].cost = round2(counterTab[i].cost);
        ticket_restoTab[i].cost = round2(ticket_restoTab[i].cost);
      }

      const paymentOganize = [
        stripeOrganize,
        cashOrganize,
        totalOrganize,
        tpeOrganize,
        ticket_restoOrganise,
        counterOrganise,
      ];
      const paymentTypes = [stripe, cash, total, tpe, ticket_resto, counter];
      for (let i = 0; i < paymentOganize.length; i++) {
        Object.values(paymentOganize[i]).forEach((val) =>
          paymentTypes[i].push(val),
        );
        paymentTypes[i].sort((a, b) => moment(a.date) - moment(b.date));
        Object.keys(paymentTypes[i]).forEach((day) => {
          const correctionMonth = moment(paymentTypes[i][day].date).month() + 1;
          paymentTypes[i][day].date = `${moment(
            paymentTypes[i][day].date,
          ).date()}/${correctionMonth}`;
        });
      }
    }

    if (stripe)
      Object.keys(stripe).forEach((day) => {
        stripe[day].cumulDaily = round2(stripe[day].cumulDaily);
        cash[day].cumulDaily = round2(cash[day].cumulDaily);
        total[day].cumulDaily = round2(total[day].cumulDaily);
        tpe[day].cumulDaily = round2(tpe[day].cumulDaily);
        counter[day].cumulDaily = round2(counter[day].cumulDaily);
        ticket_resto[day].cumulDaily = round2(ticket_resto[day].cumulDaily);
      });
    const groupedBilling = {};
    billing.forEach((bill) => {
      const { dateOrder: orderDate = '' } = bill;
      const orderDateObj = moment(orderDate);
      if (
        orderDateObj.isBetween(
          moment(dateRange.startDate).startOf('day'),
          moment(lastDate).endOf('day')
        )
      ) {
        if (!groupedBilling[bill.shopId]) {
          groupedBilling[bill.shopId] = [];
        }
        groupedBilling[bill.shopId].push(bill);
      }
    });
    if (isAllShops) {
      const allShopIds = _.union(_.keys(shops), _.keys(groupedBilling));
      for (const key in allShopIds) {
        const shopId = allShopIds[key];
        let totalShopTab = {};
        if (shopId in groupedBilling) {
          const shopBilling = groupedBilling[shopId];
          totalShopTab = await calculateTotalTabForShop(
            billing,
            shopBilling,
            previousPeriod,
            shopId,
          );
        } else {
          totalShopTab = {
            CA: 0,
            nbCmd: 0,
            moy: 0,
            rate: 0,
            canceledCmd: 0,
            missedCmd: 0,
          };
        }
        totalShopTab.shopId = shopId;
        shopResults.push({
          totalShopTab,
        });
      }
    } else {
      let totalShopTab = {};
      if (shopIdCache in groupedBilling) {
        const shopBilling = groupedBilling[shopIdCache];
        totalShopTab = await calculateTotalTabForShop(
          billing,
          shopBilling,
          previousPeriod,
          shopIdCache,
        );
      } else {
        totalShopTab = {
          CA: 0,
          nbCmd: 0,
          moy: 0,
          rate: 0,
          canceledCmd: 0,
          missedCmd: 0,
        };
      }
      totalShopTab.shopId = shopIdCache;
      shopResults.push({
        totalShopTab,
      });
    }
  } catch (error) {
    console.error(error);
    store.dispatch(openErrorModal(ERROR_GET_FINANCES));
  }

  return {
    stripe,
    cash,
    total,
    tpe,
    counter,
    ticket_resto,
    stripeTab,
    cashTab,
    totalTab,
    tpeTab,
    ticket_restoTab,
    counterTab,
    details,
    shopResults,
  };
};
const followSupport = async (dispatch) => {
  const onValueChange = (snapshot) => {
    const supportData = snapshot.val() ? snapshot.val() : null;
    dispatch({
      type: SET_SUPPORT,
      payload: supportData,
    });
  };
  await initializedApp
    .database()
    .ref('/support')
    .on('value', onValueChange);
};

export const changePrinter = async (printers) => {
  await updateShopPrinter(printers);
};

export const initializeApp = async () => {
  if (
    !initializedApp ||
    (initializedApp && initializedApp.name !== shopIdCache)
  ) {
    const id = new Cookies().get('shopId');
    if (!shopConfig) {
      await setShopId(id);
      shopConfig = firebaseConfigsForShops[shopIdCache];
    }
    try {
      initializedApp =
        firebaseConfigsForShops[shopIdCache] &&
          _.find(app.apps, (firebaseApp) => firebaseApp.name === shopIdCache)
          ? app.app(shopIdCache)
          : app.initializeApp(shopConfig, shopIdCache);
    } catch (error) {
      await app.app(shopIdCache).delete();
      initializedApp = app.initializeApp(shopConfig, shopIdCache);
    }
    return initializedApp;
  }
};

export const initializeDeliveryBDD = async () => {
  if (!initializedDeliveryBDD) {
    initializedDeliveryBDD = app.initializeApp(
      firebaseConfigsForShops.fleet,
      'fleet',
    );
  }
  return initializedDeliveryBDD;
};

export const getIdToken = async (initializedApp, forceRefresh = false) => {
  if (typeof initializedApp === 'undefined')
    initializedApp = store.getState()?.user?.initializedApp;
  try {
    if (!initializedApp?.auth) {
      return null;
    }
    const now = Date.now();
    if (!forceRefresh && cachedToken && now < expiresAt - SKEW_MS) {
      return cachedToken;
    }
    const tokenResult = await initializedApp
      .auth()
      ?.currentUser.getIdTokenResult(forceRefresh);
    cachedToken = tokenResult.token;
    expiresAt = new Date(tokenResult.expirationTime).getTime();
    return cachedToken;
  } catch {
    return null;
  }
};

// FORCE refresh token at least once per hour, that's the firebase token life
// => https://firebase.google.com/docs/auth/admin/manage-sessions
setInterval(() => {
  console.log(`Refreshing token`);
  failSafe(() => {
    getIdToken(undefined, true);
  });
}, 60 * 60 * 1000 - SKEW_MS);

export const getFirebaseCredentials = async (shopId) => {
  if (firebaseConfigsForShops[shopId]) return;
  let firebaseCredentials = {};
  try {
    firebaseCredentials = await getCompanyFirebaseCredentials(shopId);
    Object.assign(firebaseConfigsForShops, firebaseCredentials);
  } catch (err) {
    applicationError(ERROR_NO_ACCESS, { err, shopId, firebaseCredentials });
  }
};
export const getRoles = async () => {
  const companyId = firebaseConfigsForShops[shopIdCache]?.companyId;
  const customerId = getFirebaseCustomerId();
  const idToken = await getIdToken(initializedApp);
  let roles = {};
  try {
    roles = await getRolesAndPermissions(companyId, customerId, idToken);
    return roles;
  } catch (err) {
    applicationError(ERROR_NO_ACCESS, { err, shopIdCache, roles });
  }
};
export async function initializeSentry(shopId) {
  let shop = shopIdCache;
  if (!shopIdCache) {
    await setShopId(new Cookies().get('shopId')); // TODO not needed ??
    shop = shopIdCache;
  }
  if (shopId) shop = shopId;

  if (isProd()) {
    Sentry.init({
      dsn: SENTRY.DSN,
      environment: shop,
    });
  }
  return true;
}

export const changeDesignWeb = async (
  image,
  sectionOrientation,
  productStyle,
  cartPhrase,
) => {
  if (isStringNotNull(image) && !image.includes('storage'))
    image = await uploadImage(image);
  await updateDesignWeb({
    sectionOrientation,
    productStyle,
    imageCoverOrderUrl: image,
    cartPhrase,
  });
};

export const changeMobileDesign = async (data, config) => {
  const {
    pictures,
    fedilityPicture,
    titleDescription,
    clientSpace,
    url,
    facebook,
    instagram,
    linkedin,
    productStyle,
    products,
    name,
    newProductsDescription,
    myAccountImage,
    myOrdersImage,
    enabled,
    splashBackgroundColor
  } = data
  let fedilityImage = isStringNotNull(fedilityPicture) ? fedilityPicture : null;
  if (fedilityImage && !fedilityImage.includes('storage'))
    fedilityImage = await uploadImage(fedilityPicture);

  let myAccountPicture = isStringNotNull(myAccountImage) ? myAccountImage : null;
  if (myAccountPicture && !myAccountPicture.includes('storage'))
    myAccountPicture = await uploadImage(myAccountImage);

  let myOrdersPicture = isStringNotNull(myOrdersImage) ? myOrdersImage : null;
  if (myOrdersPicture && !myOrdersPicture.includes('storage'))
    myOrdersPicture = await uploadImage(myOrdersImage);
  const picturesArray = Array.isArray(pictures) ? pictures : [];

  const promiseArray = _.map(picturesArray, async element => {
    try {
      let image = element;
      if (isStringNotNull(image) && !image.includes('storage')) {
        image = await uploadImage(image);
      }
      return isStringNotNull(image) ? image : null;
    } catch (err) {
      console.error('Error uploading slide image:', err);
      return isStringNotNull(element) ? element : null;
    }
  });

  const images = await Promise.all(promiseArray);
  const validSlides = images.filter(img => isStringNotNull(img));

  const updates = {
    mobileApp: {
      titleDescription,
      clientSpace,
      design: productStyle,
      newProductsDescription,
      enabled,
      splashBackgroundColor,
    },
    url,
    socials: { facebook, instagram, linkedin },
    loyalty: { name },
    images: {
      slides: validSlides,
      myLoyalty: fedilityImage,
      myOrders: myOrdersPicture,
      myAccount: myAccountPicture,
    },
  }
  config === 'Produits' ?
    await updateNewProducts({ newProducts: products }) : await updateDesignMobile(updates)


};

export const setCartPhrase = async (cartPhrase) => {
  await updateWebappCartPhrase(cartPhrase);
};

const setPushNotificationsRealTime = (dispatch) => {
  const onValueChange = (snapshot) => {
    const pushNotifications = snapshot.val() || null;
    dispatch({
      type: SET_FIREBASE,
      payload: pushNotifications,
      id: 'pushNotifications',
    });
  };
  initializedApp
    .database()
    .ref('pushNotifications')
    .on('value', onValueChange);
  return true;
};

export const fetchHotlineAvailability = () => async (dispatch) => {
  const response = await getHotlineAvailability();
  dispatch({
    type: SET_FIREBASE,
    payload: response,
    id: 'hotlineAvailability',
  });
};
export const unlistenFirebase = async () => {
  initializedApp
    .database()
    .ref('/support')
    .off('value');
  initializedApp
    .database()
    .ref('/shops')
    .off('value');
  initializedApp
    .database()
    .ref('/users')
    .off('value');
  initializedDeliveryBDD
    .database()
    .ref('/externalDrivers')
    .off('value');
  initializedApp
    .database()
    .ref('/drivers')
    .off('value');
  initializedApp
    .database()
    .ref('/orders')
    .off('value');
  initializedApp
    .database()
    .ref('/configuration')
    .off('value');
  initializedApp
    .database()
    .ref('/pastOrders')
    .off('value');
  initializedApp
    .database()
    .ref('/canceledOrder')
    .off('value');
  initializedApp
    .database()
    .ref('/menus')
    .off('value');
  initializedApp
    .database()
    .ref('/pushNotifications')
    .off('value');
};
